import React, { useState, useEffect, useRef } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Table, Button, Input, Popconfirm, Card, Modal, message, Tabs, Pagination } from "antd";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import DeleteIcon from "../../../Images/user-management-delete-icon.png";
import { Add_Update_Designation, Current_Designation, Get_Designation_Users_Count } from "../../../services/userAPI"; // Adjust path
import { useLocation, useNavigate } from "react-router-dom";
import GenderConfiguration from "../../Dashboard/Darts/masterConfiguration/genderConfigurations";
import EmployementTypeConfiguration from "../../Dashboard/Darts/masterConfiguration/employementType";
import "../../Dashboard/Darts/masterConfiguration/mastersConfiguration.css";
import "../../Dashboard/Darts/masterConfiguration/genderConfiguration.css";
import ThresholdsConfiguration from "../../Dashboard/Darts/masterConfiguration/thresholdsConfiguration";
import PageBreadcrumb from '../../../reusableField/PageBreadcrumb';

const { TabPane } = Tabs;

const AdminMastersConfiguration = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState(""); // For search in Masters Configuration
    const [currentPage, setCurrentPage] = useState(1); // Pagination state
    const [pageSize, setPageSize] = useState(5); // Set page size to 5
    const [editingKey, setEditingKey] = useState(null); // For handling row edit
    const [editedName, setEditedName] = useState(""); // For storing edited name
    const [isModalVisible, setIsModalVisible] = useState(false); // Modal visibility
    const [newDesignation, setNewDesignation] = useState("");
    const [deleteId, setDeleteId] = useState(null);
    const inputRef = useRef(null);

    const [designationEntries, setDesignationEntries] = useState([{ name: "" }]);

    const handleBack = () => {
        navigate("/admin-home"); // This will navigate back to the previous page
    }

    const handleDiscardClick = () => {
        navigate("/admin-home");
    };


    const getActiveTabFromPath = () => {
        if (location.pathname.includes("user-management")) return "user-management";
        return "masters-configuration";
    };
    const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
    const name = location.state?.name;
    const accountName = location.state?.accountName;
    const roleId = location.state?.roleId;
    const accountId = location.state?.accountId;

    useEffect(() => {
        setActiveTab(getActiveTabFromPath());
    }, [location.pathname]);

    const handleNextToProductConfiguration = () => {
        if (activeTab === "masters-configuration") {
            navigate("/admin-user-management", { state: { roleId, accountId, name, accountName } });
        } else if (activeTab === "user-management") {
            navigate("/admin-masters-configuration", { state: { roleId, accountId, name, accountName } });
        }
    };

    // Fetch data using GraphQL query
    const { loading, data, refetch } = useQuery(Current_Designation, {
        variables: {
            jobroleId: roleId,
            accountId: accountId,
        },
    });

    // Local state to store the fetched data
    const [fetchedData, setFetchedData] = useState([]);

    // UseEffect to store the fetched data in local state
    useEffect(() => {
        if (data) {
            const reversedData = [...(data?.getDesignationsJobroleid || [])].reverse();
            setFetchedData(reversedData);
        }
    }, [data]);

    useEffect(() => {
        if (isModalVisible && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [isModalVisible]);

    // Filter data based on search term
    const filteredData = fetchedData.filter((item) =>
        item.designation.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Paginate the filtered data
    const paginatedData = filteredData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page when search term changes
    };

    const handleNextPage = () => {
        setCurrentPage((prev) => prev + 1);
    };

    const handlePreviousPage = () => {
        setCurrentPage((prev) => prev - 1);
    };

    const handleSave = (key) => {
        setEditingKey(null); // Reset the editing state
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setDesignationEntries([{ name: "" }]);
    };

    const [addUpdateDesignation, { loading: addLoading }] = useMutation(Add_Update_Designation);
    const [checkDesignationCount] = useLazyQuery(Get_Designation_Users_Count, { fetchPolicy: "network-only" });

    const handleAddDesignation = async () => {
        const validEntries = designationEntries.filter((d) => d.name.trim() !== "");

        if (validEntries.length > 10) {
            message.warning("You can add a maximum of 10 designations at once.");
            return;
        }

        if (validEntries.length === 0) {
            message.warning("Please enter at least one valid designation.");
            return;
        }

        try {
            for (let entry of validEntries) {
                const response = await addUpdateDesignation({
                    variables: {
                        accountId: Number(accountId),
                        jobroleId: Number(roleId),
                        designationName: entry.name.trim(),
                        designationId: null,
                    },
                    refetchQueries: [{ query: Current_Designation }],
                });

                if (!response.data.addUpdateDesignations.success) {
                    message.error(response.data.addUpdateDesignations.message || "Error adding: " + entry.name);
                    return;
                }
            }

            message.success("Designations added successfully!");
            setIsModalVisible(false);
            setDesignationEntries([{ name: "" }]);
            refetch();
        } catch (error) {
            console.error("Error adding designations:", error);
            message.error("Failed to add designations.");
        }
    };

    const handleDelete = async (designationId, designationName) => {
        setDeleteId(designationId);
        try {
            const countResponse = await checkDesignationCount({
                variables: {
                    accountId: Number(accountId),
                    jobroleId: Number(roleId),
                    designationId: designationId,
                },
            });
            const count = countResponse?.data?.getDesignationUsersCount ?? 0;
            if (count > 0) {
                message.error(`Cannot delete: ${count} user(s) are assigned to this designation.`);
                setDeleteId(null);
                return;
            }

            const response = await addUpdateDesignation({
                variables: {
                    accountId: Number(accountId),
                    jobroleId: Number(roleId),
                    designationName: designationName,
                    designationId: designationId,
                    status: 0,
                },
            });

            if (response.data.addUpdateDesignations.success) {
                message.success("Designation deleted successfully!");
                refetch();
            } else {
                message.error("Error: " + response.data.addUpdateDesignations.message);
            }
        } catch (error) {
            message.error("Failed to delete designation");
        } finally {
            setDeleteId(null);
        }
    };

    const columns = [
        {
            title: "Designation",
            dataIndex: "designation",
            key: "designation",
            render: (text, record) =>
                editingKey === record.key ? (
                    <Input
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onPressEnter={() => handleSave(record.key)}
                    />
                ) : (
                    text
                ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
                    {editingKey === record.key ? (
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => handleSave(record.key)}
                            style={{ marginRight: 8 }}
                        />
                    ) : (
                        <Popconfirm
                            title="Are you sure you want to delete this?"
                            onConfirm={() => handleDelete(record.id, record.designation)}
                            okText={deleteId === record.id ? " " : "Yes"}
                            cancelText="No"
                            okButtonProps={{
                                loading: deleteId === record.id,
                                style: {
                                    backgroundColor: "#d32f2f", // Red background for "Yes" (dangerous action)
                                    color: "#fff", // White text for contrast
                                    width: "80px", // Custom width
                                    height: "40px", // Custom height
                                    border: "none", // Optional: remove default border
                                    borderRadius: "4px", // Optional: rounded corners
                                },
                            }}
                            cancelButtonProps={{
                                style: {
                                    backgroundColor: "#fff", // Gray background for "No" (neutral action)
                                    color: "black", // White text for contrast
                                    width: "80px", // Custom width
                                    height: "40px", // Custom height
                                    border: "1px solid black", // Optional: remove default border
                                    borderRadius: "4px", // Optional: rounded corners

                                },
                            }}
                        >
                            <img src={DeleteIcon} alt="Delete" style={{ cursor: "pointer" }} />
                        </Popconfirm>
                    )}
                </div>
            ),
            align: "right",
        },
    ];


    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;

    const handleBackToViewRole = () => {
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate('/admin-view-role', {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution:
                    mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    return (
        <div className="user-management-container">
            <PageBreadcrumb homePath="/admin">
                <span onClick={handleBack} style={{ cursor: 'pointer' }}>{accountName}</span>
                <span>&gt;</span>
                <span onClick={() => handleBackToViewRole({ state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded } })} style={{ cursor: 'pointer' }}>{name || 'Unknown Role'}</span>
                <span>&gt;</span>
                <span style={{ fontWeight: 'bold' }}>Master configuration</span>
            </PageBreadcrumb>
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key);
                    if (key === "masters-configuration") {
                        navigate("/masterConfiguration", { state: { roleId, accountId, name, accountName } });
                    } else {
                        navigate("/admin-user-management", { state: { roleId, accountId, name, accountName } });
                    }
                }}
                centered
            >
                <TabPane tab="Masters Configuration" key="masters-configuration">
                    <Card className="search-card">
                        <label
                            style={{
                                fontFamily: "Inter",
                                fontSize: "24px",
                                fontWeight: 500,
                                lineHeight: "100%",
                                letterSpacing: "-1%",
                                marginBottom: "2%",
                            }}
                        >
                            Designation
                        </label>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "20px",
                                borderBottom: "none",
                                marginTop: "1%",
                            }}
                        >
                            <div className="search-group" style={{ flex: 1 }}>
                                <div className="search-input-wrapper">
                                    <Input
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        placeholder="Search designation"
                                        className="search-input-masters-configuration"
                                        suffix={<SearchOutlined />}
                                    />
                                </div>
                            </div>
                            <div>
                                <button
                                    className="add-designation-btn"
                                    onClick={() => setIsModalVisible(true)}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    Add Designation
                                </button>
                            </div>
                        </div>
                        <br />
                        <Table
                            columns={columns}
                            dataSource={paginatedData.map((item, index) => ({
                                ...item,
                                key: index,
                            }))}
                            pagination={false}
                            loading={loading}
                            showHeader={false}
                        />
                        {filteredData.length > pageSize && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={filteredData.length}
                                    onChange={(page, size) => {
                                        setCurrentPage(page);
                                        setPageSize(size);
                                    }}
                                    // showSizeChanger
                                    // pageSizeOptions={['5', '10', '20', '50']}
                                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                />
                            </div>
                        )}
                    </Card>
                    <Modal
                        title="Add New Designation"
                        visible={isModalVisible}
                        onCancel={() => setIsModalVisible(false)}
                        footer={null}
                    >
                        <div>
                            {designationEntries.map((entry, index) => (
                                <div key={index} style={{ display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center" }}>
                                    <Input
                                        value={entry.name}
                                        autoFocus={index === designationEntries.length - 1}
                                        onChange={(e) => {
                                            const newEntries = [...designationEntries];
                                            newEntries[index].name = e.target.value;
                                            setDesignationEntries(newEntries);
                                        }}
                                        placeholder={`Designation ${index + 1}`}
                                        ref={index === designationEntries.length - 1 ? inputRef : null}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleAddDesignation();
                                            }
                                        }}
                                        style={{ flex: 1 }}
                                    />
                                    {index > 0 && (
                                        <Button
                                            danger
                                            type="text"
                                            onClick={() => {
                                                const newEntries = designationEntries.filter((_, i) => i !== index);
                                                setDesignationEntries(newEntries);
                                            }}
                                            style={{ padding: "4px 8px" }}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            ))}
                            <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "10px" }}>
                                {designationEntries.length} field(s) added {designationEntries.length >= 10 ? "- Maximum limit reached" : ""}
                            </div>
                            <div
                                style={{
                                    marginTop: "20px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <button
                                    className="genderAddNewButton"
                                    // type="dashed"
                                    onClick={() => {
                                        if (designationEntries.length < 10) {
                                            setDesignationEntries([...designationEntries, { name: "" }]);
                                        } else {
                                            message.warning("You cannot add more than 10 designations.");
                                        }
                                    }}
                                >
                                    {designationEntries.length < 10 ? "Add New" : "Not Available"}
                                </button>
                                <div>
                                    <button
                                        className="gender-configuration-cancel-button"
                                        onClick={handleCancel}
                                        style={{ marginRight: 8 }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="masters-configuration-add-button"
                                        onClick={handleAddDesignation}
                                        disabled={addLoading}
                                        style={{ opacity: addLoading ? 0.7 : 1, cursor: addLoading ? 'not-allowed' : 'pointer' }}
                                    >
                                        {addLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Modal>
                    <br />
                    <GenderConfiguration roleId={roleId} accountId={accountId} />
                    <br />
                    <EmployementTypeConfiguration roleId={roleId} accountId={accountId} />
                    <br />
                    <ThresholdsConfiguration onSaveSuccess={handleNextToProductConfiguration} />
                </TabPane>
                <TabPane tab="User Management" key="user-management"></TabPane>
            </Tabs>
        </div>
    );
};

export default AdminMastersConfiguration;